import { useState, useRef } from 'react';
import { Modal, Button } from '@components/ui';
import { toast } from 'react-toastify';
import { Paperclip, X, Lock, Package } from 'lucide-react';
import MaterialModalRouter, { MATERIAL_SUBCATEGORIES } from '@components/shared/MaterialModalRouter';

export default function ScheduledPaymentModal({ isOpen, onClose, onSubmit, payment = null, activeTab = 'debts' }) {
    const isEdit = !!payment;
    const [formData, setFormData] = useState({
        description: payment?.description || '',
        category: payment?.category || '',
        amount: payment?.amount || '',
        frequency: payment?.frequency || 'one-time',
        nextDueDate: payment?.nextDueDate || '',
        supplierId: payment?.supplierId || '',
        supplierName: payment?.supplierName || '',
        paymentSubtype: payment?.paymentSubtype || 'havale',
    });
    const [receiptFile, setReceiptFile] = useState(null);
    const fileInputRef = useRef(null);

    // Senaryo B: Malzeme Alımı entegrasyonu
    const [materialSubcategory, setMaterialSubcategory] = useState('');
    const [materialModalOpen, setMaterialModalOpen] = useState(false);
    const [materialData, setMaterialData] = useState(null);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleMaterialSubmit = (data) => {
        setMaterialData(data);
        setMaterialModalOpen(false);
        handleChange('amount', data.calculatedTotal);
        handleChange('supplierId', data.selectedCompany?.id || '');
        handleChange('supplierName', data.selectedCompany?.name || '');
        handleChange('description', data.autoDescription);
    };

    const handleSubmit = () => {
        onSubmit?.({
            ...formData,
            materialOrderData: materialData,
            receiptUrl: receiptFile ? receiptFile.name : null,
        });
        toast.success(isEdit ? 'İşlem güncellendi.' : 'Yeni kayıt başarıyla eklendi.');
        onClose();
    };

    const modalTitle = activeTab === 'debts'
        ? (isEdit ? 'Borç/Fatura Düzenle' : 'Yeni Borç Kaydı')
        : (isEdit ? 'Sabit Gider Düzenle' : 'Yeni Sabit Gider');

    const inputClass = "w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50";
    const isMalzeme = formData.category === 'Malzeme Alımı';

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={modalTitle}
                size="md"
                footer={
                    <>
                        <Button variant="ghost" onClick={onClose}>İptal</Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={isMalzeme && !materialData}>
                            {isEdit ? 'Güncelle' : 'Kaydet'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    {/* Açıklama */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            {activeTab === 'debts' ? 'Alınan Hizmet/Ürün Açıklaması *' : 'Gider Açıklaması *'}
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                className={`${inputClass} ${materialData ? 'pr-8' : ''}`}
                                placeholder={activeTab === 'debts' ? "Örn: A Blok Demir Alımı" : "Örn: Aylık Muhasebe Ücreti"}
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                            />
                            {materialData && <Lock size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted" />}
                        </div>
                    </div>

                    {/* Kategori + Tedarikçi */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Kategori</label>
                            <select
                                className={inputClass}
                                value={formData.category}
                                onChange={(e) => {
                                    handleChange('category', e.target.value);
                                    if (e.target.value !== 'Malzeme Alımı') {
                                        setMaterialData(null);
                                        setMaterialSubcategory('');
                                    }
                                }}
                            >
                                <option value="">Seçiniz</option>
                                {activeTab === 'debts' ? (
                                    <>
                                        <option value="Malzeme Alımı">Malzeme Alımı</option>
                                        <option value="Taşeron Hakedişi">Taşeron Hakedişi</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="İşçilik">İşçilik</option>
                                        <option value="Fatura">Fatura (Elektrik/Su vb)</option>
                                        <option value="Sigorta">Sigorta / Vergi</option>
                                    </>
                                )}
                            </select>
                        </div>
                        {activeTab === 'debts' && !isMalzeme && (
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Tedarikçi / Firma</label>
                                <input
                                    type="text"
                                    placeholder="Firma adını girin..."
                                    className={inputClass}
                                    value={formData.supplierName}
                                    onChange={(e) => handleChange('supplierName', e.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Malzeme Alımı: Alt kategori seçici + modal açıcı */}
                    {isMalzeme && (
                        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
                            <p className="text-xs font-semibold text-primary">Malzeme Detayları</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Malzeme Türü <span className="text-error">*</span></label>
                                    <select
                                        className={inputClass}
                                        value={materialSubcategory}
                                        onChange={(e) => { setMaterialSubcategory(e.target.value); setMaterialData(null); }}
                                    >
                                        <option value="">Seçiniz</option>
                                        {MATERIAL_SUBCATEGORIES.map(s => (
                                            <option key={s.key} value={s.key}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <Button
                                        variant={materialData ? 'ghost' : 'primary'}
                                        onClick={() => setMaterialModalOpen(true)}
                                        disabled={!materialSubcategory}
                                        className="w-full"
                                    >
                                        <Package size={14} className="mr-1" />
                                        {materialData ? 'Tekrar Düzenle' : 'Detayları Gir'}
                                    </Button>
                                </div>
                            </div>
                            {materialData && (
                                <div className="p-2 bg-success/5 border border-success/20 rounded text-xs text-success font-medium">
                                    Malzeme detayları alındı — {materialData.autoDescription}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tedarikçi — malzeme modundan auto-fill */}
                    {isMalzeme && materialData && (
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Tedarikçi / Firma</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className={`${inputClass} pr-8 bg-bg-tertiary`}
                                    value={formData.supplierName}
                                    readOnly
                                />
                                <Lock size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted" />
                            </div>
                        </div>
                    )}

                    {/* Sıklık */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            {activeTab === 'debts' ? 'Borç Tipi / Sıklığı' : 'Ödeme Sıklığı'}
                        </label>
                        <select
                            className={inputClass}
                            value={formData.frequency}
                            onChange={(e) => handleChange('frequency', e.target.value)}
                        >
                            <option value="one-time">Tek Seferlik (Fatura)</option>
                            <option value="taksitli">Taksitli Borç</option>
                            {activeTab !== 'debts' && (
                                <>
                                    <option value="monthly">Aylık Düzenli</option>
                                    <option value="weekly">Haftalık Düzenli</option>
                                </>
                            )}
                        </select>
                    </div>

                    {/* Tutar + Vade */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Tutar (₺)</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    className={`${inputClass} ${materialData ? 'pr-8 bg-bg-tertiary' : ''}`}
                                    value={formData.amount}
                                    readOnly={!!materialData}
                                    onChange={(e) => !materialData && handleChange('amount', e.target.value)}
                                />
                                {materialData && <Lock size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted" />}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                {activeTab === 'debts' ? 'Vade Tarihi' : 'Sonraki Ödeme Tarihi'}
                            </label>
                            <input
                                type="date"
                                className={inputClass}
                                value={formData.nextDueDate}
                                onChange={(e) => handleChange('nextDueDate', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Ödeme Aracı */}
                    {activeTab === 'debts' && (
                        <div className="p-4 bg-bg-secondary rounded-lg border border-border-light">
                            <label className="block text-sm font-medium text-text-secondary mb-2">Ödeme Aracı (Tedarikçiye Nasıl Ödenecek?)</label>
                            <select
                                className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                                value={formData.paymentSubtype}
                                onChange={(e) => handleChange('paymentSubtype', e.target.value)}
                            >
                                <option value="nakit">Nakit</option>
                                <option value="havale">Banka Havalesi / EFT</option>
                                <option value="kart">Kredi / Banka Kartı</option>
                                <option value="kendi_cek">Kendi Çekimiz (Verilen)</option>
                                <option value="ciro_cek">Müşteri Çeki (Ciro Edilecek)</option>
                                <option value="senet">Senet</option>
                            </select>
                            <p className="text-xs text-text-muted mt-2">
                                * "Kendi Çekimiz" seçilirse Çek Takibi'nde otomatik "Verilen Çek" kaydı oluşturulacaktır.
                            </p>
                        </div>
                    )}

                    {/* Belge / Dekont */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            <Paperclip size={13} className="inline mr-1" />
                            Ödeme Belgesi / Dekont
                        </label>
                        {receiptFile ? (
                            <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary rounded-lg border border-border-light">
                                <Paperclip size={14} className="text-primary shrink-0" />
                                <span className="text-sm text-text-primary flex-1 truncate">{receiptFile.name}</span>
                                <button type="button" onClick={() => setReceiptFile(null)} className="text-text-muted hover:text-error">
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full px-3 py-2 rounded-lg border border-dashed border-border-light text-sm text-text-muted hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
                            >
                                <Paperclip size={14} />
                                Dekont, Fiş veya Fatura Seç...
                            </button>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={e => setReceiptFile(e.target.files[0] || null)}
                        />
                    </div>
                </div>
            </Modal>

            {/* Senaryo B: Malzeme modal overlay — MaterialModalRouter */}
            <MaterialModalRouter
                subcategory={materialSubcategory}
                isOpen={materialModalOpen}
                onClose={() => setMaterialModalOpen(false)}
                onMaterialSubmit={handleMaterialSubmit}
            />
        </>
    );
}
